"use client";

import React, { useState, useRef, useEffect } from 'react';
import { MapPin, X, LocateFixed } from '@/components/icons';
import { PlaneLanding, PlaneTakeoff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { useSidebarWidth } from '@/hooks/useSidebarWidth';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import { motion, AnimatePresence } from 'framer-motion';
import LocationAutocomplete from './LocationAutocomplete';
import MapPinSelector from './MapPinSelector';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { NoteLocation } from '@/types/location';
import type { LatLng } from '@/hooks/useRequestRide';
import { cn } from '@/lib/utils';

interface Address {
  id: string;
  displayName: string;
  coords: LatLng;
  floor?: string | null;
  apartment?: string | null;
}

interface AddressPickerSidebarProps {
  isOpen: boolean;
  addressType: 'pickup' | 'delivery' | null;
  onClose: () => void;
  onSelectAddress: (address: Address) => void;
  userAddresses: UserAddress[];
  sessionUserId?: string;
  initialLocation?: { lat: number; lng: number } | null;
  initialDisplayName?: string | null;
  forceAlturaZero?: boolean;
  overlayZIndex?: number;
  panelZIndex?: number;
  fullViewport?: boolean;
}

const AddressPickerSidebarComponent = React.forwardRef<HTMLDivElement, AddressPickerSidebarProps>(
  (
    {
      isOpen,
      addressType,
      onClose,
      onSelectAddress,
      userAddresses,
      sessionUserId,
      initialLocation,
      initialDisplayName,
      forceAlturaZero = false,
      overlayZIndex = 9998,
      panelZIndex = 9999,
      fullViewport = false
    },
    ref
  ) => {
  const toFiniteNumber = (value: unknown): number | null => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const { maxWidth } = useSidebarWidth();
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();
  const { toast } = useToast();
  const internalRef = useRef<HTMLDivElement>(null);
  const sidebarRef = ref || internalRef;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const usedGpsRef = useRef(false); // Track if GPS was used to prevent initialLocation override

  const [inputLocation, setInputLocation] = useState<NoteLocation | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [loadingGps, setLoadingGps] = useState(false);
  const [details, setDetails] = useState({
    streetName: '',
    altura: '',
    floor: '',
    apartment: '',
    notes: ''
  });

  // Keep a ref to details to access them in async callbacks without closure issues
  const detailsRef = useRef(details);
  useEffect(() => {
    detailsRef.current = details;
  }, [details]);

  const [showAlturaError, setShowAlturaError] = useState(false);
  const [focusCounter, setFocusCounter] = useState(0); // Counter to force LocationAutocomplete remount and refocus

  // Reset state when sidebar opens or address type changes, but preserve initialLocation if provided
  useEffect(() => {
    if (isOpen && addressType) {
      // Reset GPS flag when sidebar opens or address type changes
      usedGpsRef.current = false;
      
      // If initialLocation is NOT provided (fresh open), reset everything
      if (!initialLocation) {
        setCoords(null);
        setInputLocation(null);
        setDetails({
          streetName: '',
          altura: '',
          floor: '',
          apartment: '',
          notes: ''
        });
      }
      
      setLoadingGps(false);
      setShowAlturaError(false);
      
      // Increment counter to force LocationAutocomplete remount and trigger autoFocus
      setFocusCounter(prev => prev + 1);
      
      // Recalculate header offset to ensure correct height on Safari
      recalculate();
    }
  }, [isOpen, addressType, initialLocation]); // Removed initialDisplayName from deps to avoid double-reset when both change

  // Separate effect for recalculating when sidebar opens (don't include recalculate in deps)
  useEffect(() => {
    if (isOpen && addressType) {
      const timeoutId = setTimeout(() => recalculate(), 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, addressType]);

  // Set coords from initialLocation when it's provided and populate details
  // BUT: Skip if user has just used GPS to prevent saved address data from overriding GPS
  useEffect(() => {
    // Only run if we have initialLocation AND we haven't manually triggered GPS recently
    if (initialLocation && isOpen && addressType && !usedGpsRef.current) {
      const { lat, lng } = initialLocation;
      const normalizedLat = toFiniteNumber(lat);
      const normalizedLng = toFiniteNumber(lng);
      if (normalizedLat === null || normalizedLng === null) {
        console.error('[AddressPickerSidebar] Invalid initial coordinates:', initialLocation);
        return;
      }

      console.log('Populating from initialLocation:', { lat, lng, initialDisplayName });
      
      setCoords({ lat: normalizedLat, lon: normalizedLng });
      setLoadingGps(false);
      
      // If initialDisplayName is provided (from saved address), use it directly
      if (initialDisplayName) {
        const { altura, streetName } = extractAddressComponents(initialDisplayName);
        setInputLocation({
          latitude: normalizedLat,
          longitude: normalizedLng,
          displayName: initialDisplayName
        });
        setDetails({
          streetName: streetName || '',
          altura: forceAlturaZero ? '0' : (altura || ''),
          floor: '',
          apartment: '',
          notes: ''
        });
      } else {
        // Otherwise, fetch from reverse geocoding
        const fetchAddressDetails = async () => {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const data = await response.json();
            if (data.display_name && !usedGpsRef.current) { // Check ref again before setting
              const { altura, streetName } = extractAddressComponents(data.display_name, data);
              setInputLocation({
                latitude: normalizedLat,
                longitude: normalizedLng,
                displayName: data.display_name
              });
              setDetails({
                streetName: streetName || '',
                altura: forceAlturaZero ? '0' : (altura || ''),
                floor: '',
                apartment: '',
                notes: ''
              });
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }
        };
        
        fetchAddressDetails();
      }
    }
  }, [initialLocation, initialDisplayName, isOpen, addressType, forceAlturaZero]);

  useEffect(() => {
    if (!isOpen || !coords || !forceAlturaZero) return;
    if (details.altura.trim()) return;

    setDetails((prev) => ({ ...prev, altura: '0' }));
    setShowAlturaError(false);
  }, [coords, details.altura, forceAlturaZero, isOpen]);

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS no disponible', description: 'Tu navegador no soporta geolocalización.' });
      return;
    }

    // First, clear all previous state completely to ensure clean GPS lookup
    setCoords(null);
    setInputLocation(null);
    setDetails({
      streetName: '',
      altura: '',
      floor: '',
      apartment: '',
      notes: ''
    });
    setShowAlturaError(false);
    setLoadingGps(true);

    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log('GPS Coord obtained:', { latitude, longitude });
          
          // Mark that we're using GPS immediately to block other effects
          usedGpsRef.current = true;
          
          // Try to get address from coordinates using reverse geocoding
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );
            const data = await response.json();
            if (data.display_name) {
              const { altura, streetName } = extractAddressComponents(data.display_name, data);
              console.log('GPS Reverse Geocode success:', { streetName, altura });
              // Update details with fresh data from GPS reverse geocoding
              setDetails({
                streetName: streetName || '',
                altura: altura || '',
                floor: '',
                apartment: '',
                notes: ''
              });
              // Set input location info
              setInputLocation({
                latitude,
                longitude,
                displayName: data.display_name
              });
              // Finally set coords to display map with refine fields
              setCoords({ lat: latitude, lon: longitude });
            } else {
              // Fallback if no display name - still clean refine fields
              setDetails({
                streetName: '',
                altura: '',
                floor: '',
                apartment: '',
                notes: ''
              });
              setInputLocation({ latitude, longitude, displayName: 'Mi ubicación actual' });
              setCoords({ lat: latitude, lon: longitude });
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            // Clean refine fields on error too
            setDetails({
              streetName: '',
              altura: '',
              floor: '',
              apartment: '',
              notes: ''
            });
            setInputLocation({ latitude, longitude, displayName: 'Mi ubicación actual' });
            setCoords({ lat: latitude, lon: longitude });
          }
          setLoadingGps(false);
        },
        (error) => {
          console.error('GPS error:', error);
          toast({ title: 'Error GPS', description: 'No fue posible obtener la ubicación.' });
          setLoadingGps(false);
        }
      );
    } catch (error) {
      console.error('GPS error:', error);
      toast({ title: 'Error GPS', description: 'Ocurrió un error al obtener la ubicación.' });
      setLoadingGps(false);
    }
  };

  const extractAddressComponents = (displayName: string | undefined, data?: any) => {
    if (!displayName) return { altura: '', streetName: '' };
    
    // If we have raw Nominatim data, prefer it
    if (data?.address) {
      const road = data.address.road || data.address.pedestrian || data.address.suburb || '';
      const houseNumber = data.address.house_number || '';
      if (road) {
        return { altura: houseNumber, streetName: road };
      }
    }

    const parts = displayName.split(',').map(p => p.trim()).filter(Boolean);
    let altura = '';
    let streetName = '';
    if (parts.length > 0) {
      const firstPart = parts[0];
      const match = firstPart.match(/^(.*?)\s+(\d+)$/);
      if (match) {
        streetName = match[1];
        altura = match[2];
      } else if (/^\d+$/.test(firstPart) && parts.length > 1) {
        altura = firstPart;
        streetName = parts[1];
      } else {
        streetName = firstPart;
        // Check if second part is just a number (altura)
        if (parts.length > 1 && /^\d+$/.test(parts[1])) {
          altura = parts[1];
        }
      }
    }
    return { altura, streetName };
  };

  const truncateAddress = (displayName: string) => {
    if (!displayName) return '';
    const parts = displayName.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[1]} ${parts[0]}`.trim();
    }
    return parts[0] || '';
  };

  if (!isOpen || !addressType) return null;

  const title = addressType === 'pickup' ? 'Punto de Retiro' : 'Punto de Destino';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-200"
        style={{ zIndex: overlayZIndex }}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={cn(
          "fixed right-0 w-full sm:w-[400px] bg-background shadow-2xl z-[9999] flex flex-col overflow-x-hidden transition-none",
          "translate-x-0"
        )}
      style={{
        zIndex: panelZIndex,
        top: isDesktop && fullViewport ? '0' : (isDesktop ? `${headerHeight}px` : '0'),
        bottom: '0',
        height: isDesktop
          ? (fullViewport ? '100vh' : `calc(100vh - ${headerHeight}px)`)
          : '100dvh',
        pointerEvents: 'auto'
      }}
    >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-white">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="font-semibold text-lg text-white">{title}</h2>
        </div>
      </div>

      {/* Content */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden">
        <div className={coords ? "p-4 space-y-4" : "p-4 space-y-4"}>
          {/* Input LocationAutocomplete - only show if no coords */}
          {!coords && (
            <LocationAutocomplete
              value={inputLocation}
              key={`address-picker-${addressType}-${focusCounter}`}
              onChange={(loc) => {
                setInputLocation(loc);
                if (loc) {
                  const lat = toFiniteNumber(loc.latitude);
                  const lon = toFiniteNumber(loc.longitude);
                  if (lat === null || lon === null) {
                    toast({ title: 'Error', description: 'Ubicación inválida. Intenta elegir otro punto.' });
                    return;
                  }

                  const { altura, streetName } = extractAddressComponents(loc.displayName);
                  setCoords({ lat, lon });
                  setDetails({
                    streetName: loc.road || streetName,
                    altura: loc.houseNumber || altura,
                    floor: '',
                    apartment: '',
                    notes: ''
                  });
                }
              }}
              onFocus={() => {
                // No scroll needed in intermediate sidebar
              }}
              allowAnyPlace={true}
              autoFocus={true}
              showGps={false}
              placeholder={`Buscar ${title.toLowerCase()}...`}
            />
          )}

          {/* Map Picker */}
          {coords && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-3">Refina la ubicación en el mapa</p>
              <div className="rounded-lg overflow-hidden border" style={{ height: 200 }}>
                <MapPinSelector
                  lat={coords.lat}
                  lon={coords.lon}
                  onChange={(lat, lon) => setCoords({ lat, lon })}
                  height="200px"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Nombre de la calle</Label>
                  <Input
                    value={details.streetName}
                    onChange={(e) => setDetails(prev => ({ ...prev, streetName: e.target.value }))}
                    placeholder="Calle"
                  />
                </div>
                <div>
                  <Label className={`text-xs ${showAlturaError ? 'text-primary font-bold' : ''}`}>
                    Altura{forceAlturaZero ? '' : ' *'}
                  </Label>
                  <Input
                    value={details.altura}
                    onChange={(e) => {
                      setDetails(prev => ({ ...prev, altura: e.target.value }));
                      if (e.target.value.trim()) setShowAlturaError(false);
                    }}
                    placeholder="Nro"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={showAlturaError ? 'border-primary border-2 animate-pulse' : ''}
                  />
                  {showAlturaError && !forceAlturaZero && (
                    <p className="mt-1 inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                      Completa la altura para continuar.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Piso (Opcional)</Label>
                  <Input
                    value={details.floor}
                    onChange={(e) => setDetails(prev => ({ ...prev, floor: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Depto (Opcional)</Label>
                  <Input
                    value={details.apartment}
                    onChange={(e) => setDetails(prev => ({ ...prev, apartment: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Notas:</Label>
                <Textarea
                  value={details.notes}
                  onChange={(e) => setDetails(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Descripción del lugar, Destinatario, pedidos especiales..."
                  className="h-20"
                />
              </div>
            </div>
          )}


        </div>
      </ScrollArea>

      {/* Footer with Confirm Button */}
      {coords && (
        <div className="border-t pt-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-2 bg-muted/20 shrink-0">
          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              const typedAltura = details.altura.trim();
              const resolvedAltura = forceAlturaZero ? (typedAltura || '0') : typedAltura;

              if (!forceAlturaZero && !resolvedAltura) {
                setShowAlturaError(true);
                return;
              }

              const street = details.streetName || '';
              const number = resolvedAltura;
              const floor = details.floor ? `Piso ${details.floor}` : '';
              const depto = details.apartment ? `Depto ${details.apartment}` : '';

              const finalDisplay = [street, number, floor, depto].filter(Boolean).join(' ').trim() || inputLocation?.displayName || title;
              const lat = toFiniteNumber(coords.lat);
              const lng = toFiniteNumber(coords.lon);
              if (lat === null || lng === null) {
                toast({ title: 'Error', description: 'No se pudo confirmar la ubicación. Intenta de nuevo.' });
                return;
              }

              const payload = {
                id: `${addressType}-${Date.now()}`,
                displayName: finalDisplay,
                coords: { lat, lng },
                floor: details.floor,
                apartment: details.apartment
              };
              
              console.log('[AddressPickerSidebar] 🎯 Confirming manually entered address:', payload);
              onSelectAddress(payload);
              onClose();
            }}
          >
            <span className="inline-flex items-center justify-center gap-2">
              {addressType === 'pickup' ? (
                <PlaneTakeoff className="h-4 w-4" />
              ) : (
                <PlaneLanding className="h-4 w-4" />
              )}
              Confirmar {title}
            </span>
          </Button>
        </div>
      )}
    </div>
    </>
  );
  }
);

AddressPickerSidebarComponent.displayName = 'AddressPickerSidebar';

// Export as memo-wrapped to prevent unnecessary re-renders when parent component re-renders
export const AddressPickerSidebar = React.memo(AddressPickerSidebarComponent);
